import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { uid, nowISO } from "@/lib/id";
import { uploadChunked } from "@/lib/upload";
import { hasPerm, PERSONAS } from "@/config/permissions";
import { toast } from "sonner";
import axios from "axios";
import { ccoreApi, ccoreDefault } from "@/lib/ccore";
axios.defaults.withCredentials = true;

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERSONA_KEY = "nyayyaksha_persona";

// Collections that are persisted to the backend (org-scoped domain data).
const PERSIST = new Set([
  "clients", "matters", "parties", "hearings", "tasks", "documents", "orders",
  "billing", "notes", "communications", "research", "drafts", "odr",
  "automations", "automationRuns", "opportunities", "notifications",
]);

const EMPTY_DB = {
  org: null, users: [], clients: [], matters: [], parties: [], hearings: [],
  tasks: [], documents: [], orders: [], billing: [], notes: [], communications: [],
  research: [], drafts: [], odr: [], automations: [], automationRuns: [],
  opportunities: [], notifications: [],
};
const UNDO_MS = 8000;
const CCORE_EMPTY = { ready: false, memberships: [], pendingInvites: [], activeMembershipId: null, orgId: null, orgName: null, orgType: null, homeState: null, primaryForumId: null, capabilities: [], needsPicker: false, error: null };

const normalize = (boot) => {
  const out = { ...EMPTY_DB };
  Object.keys(EMPTY_DB).forEach((k) => {
    if (k === "org") out.org = boot.org || null;
    else out[k] = boot[k] || [];
  });
  return out;
};

// --- Cookie-based session (access + refresh live in HttpOnly cookies; never in JS) ---
// Silent refresh hits /auth/refresh, which rotates the cookies server-side.
let refreshing = null;
const refreshSession = () => {
  if (!refreshing) {
    refreshing = axios.post(`${API}/auth/refresh`, {}, { _skipAuthRetry: true })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
};
axios.interceptors.response.use((r) => r, async (error) => {
  const cfg = error.config || {};
  const url = cfg.url || "";
  if (error.response?.data?.detail?.code === "MFA_REQUIRED") { window.dispatchEvent(new Event("nyk-session-ended")); }
  if (error.response?.status === 401 && !cfg._retried && !cfg._skipAuthRetry && url.startsWith(API) && !url.includes("/auth/login") && !url.includes("/auth/refresh")) {
    cfg._retried = true;
    try {
      await refreshSession();
      return axios(cfg);
    } catch (e) {
      window.dispatchEvent(new Event("nyk-session-ended"));
    }
  }
  return Promise.reject(error);
});

export function AppProvider({ children }) {
  const [db, setDb] = useState(EMPTY_DB);
  const [auth, setAuth] = useState(null);
  const [scope, setScope] = useState({ role: null, dirId: null, fullAccess: false, permissions: [], demoControls: false });
  const [ccore, setCcore] = useState(CCORE_EMPTY);
  const [bootMeta, setBootMeta] = useState({ limit: 0, truncated: {}, counts: {} });
  const [booting, setBooting] = useState(true);
  // Demo persona is a VIEW-ONLY reduction for preview environments; it can never grant more than the server role allows.
  const [personaRole, setPersonaRole] = useState(() => localStorage.getItem(PERSONA_KEY) || null);
  const ticketRef = useRef({});
  const [, setTicketRevision] = useState(0);
  const matterLoads = useRef(new Set());

  // --- C-Core (PostgreSQL + RLS) session-scoped tenant/authorization layer ---
  // Legacy login proves identity; we exchange it for a C-Core session carrying a validated
  // activeMembershipId. Best-effort: a C-Core failure never blocks the legacy workspace.
  const activateCcoreMembership = async (membershipId, userId) => {
    await ccoreApi.selectOrg(membershipId);
    const ctx = await ccoreApi.context();
    if (userId) ccoreDefault.set(userId, membershipId);
    setCcore((prev) => ({ ...prev, activeMembershipId: membershipId, orgId: ctx.orgId, orgName: ctx.orgName, orgType: ctx.orgType, homeState: ctx.homeState || null, primaryForumId: ctx.primaryForumId || null, capabilities: ctx.capabilities || [], needsPicker: false, error: null }));
    return ctx;
  };
  const initCcore = async (userId) => {
    try {
      await ccoreApi.exchange();
      const [memberships, pendingInvites] = await Promise.all([
        ccoreApi.memberships(),
        ccoreApi.myInvites().catch(() => []),
      ]);
      const active = memberships.filter((m) => m.status === "ACTIVE");
      let target = active.length === 1 ? active[0] : null;
      if (!target && active.length > 1) {
        const def = ccoreDefault.get(userId);
        target = active.find((m) => m.membershipId === def) || null;
      }
      if (target) {
        await activateCcoreMembership(target.membershipId, userId);
        setCcore((prev) => ({ ...prev, memberships, pendingInvites, ready: true, needsPicker: false }));
      } else {
        setCcore({ ...CCORE_EMPTY, memberships, pendingInvites, ready: true, needsPicker: active.length > 1 });
      }
    } catch (e) {
      setCcore({ ...CCORE_EMPTY, ready: true, error: "ccore_unavailable" });
    }
  };
  const reloadCcore = async () => {
    try {
      const [memberships, pendingInvites] = await Promise.all([
        ccoreApi.memberships(), ccoreApi.myInvites().catch(() => []),
      ]);
      setCcore((prev) => ({ ...prev, memberships, pendingInvites }));
      return { memberships, pendingInvites };
    } catch (e) { return null; }
  };
  const switchOrg = async (membershipId) => {
    try {
      const ctx = await activateCcoreMembership(membershipId, auth?.id);
      toast.success(`Switched to ${ctx.orgName}`);
      return ctx;
    } catch (e) {
      toast.error("Could not switch organisation. This membership may no longer be active.");
      throw e;
    }
  };
  const createOrg = async ({ name, orgType, homeState, primaryForumId }) =>
    ccoreApi.createOrg({ name, orgType, homeState, primaryForumId });
  const ccoreCan = (cap) => (ccore.capabilities || []).includes(cap);

  const refreshFileTicket = useCallback(async (ids = []) => {
    const paths = ids.map((id) => `/api/documents/${id}/file`);
    if (!paths.length) return;
    try {
      const { data } = await axios.post(`${API}/auth/file-tickets`, { paths });
      Object.entries(data.tickets).forEach(([path, ticket]) => { ticketRef.current[path] = { ticket, exp: Date.now() + (data.expiresIn - 60) * 1000 }; });
      setTicketRevision((n) => n + 1);
    } catch (e) { paths.forEach((path) => { delete ticketRef.current[path]; }); }
  }, []);

  const loadWorkspace = async () => {
    const meRes = await axios.get(`${API}/auth/me`);
    const bootRes = meRes.data.organisation ? await axios.get(`${API}/bootstrap`) : { data: { ...EMPTY_DB, scope: { role: null, permissions: [], fullAccess: false, demoControls: false } } };
    await refreshFileTicket((bootRes.data.documents || []).filter((d) => d.storagePath).map((d) => d.id));
    setAuth({ ...meRes.data.user, role: meRes.data.role, organisation: meRes.data.organisation });
    setDb(normalize(bootRes.data));
    if (bootRes.data.scope) setScope(bootRes.data.scope);
    if (bootRes.data.meta) setBootMeta(bootRes.data.meta);
    matterLoads.current = new Set();
    refreshFileTicket();
    if (meRes.data.user?.id) await initCcore(meRes.data.user.id);
    return meRes.data.user;
  };

  useEffect(() => {
    const onEnd = () => { setAuth(null); setDb(EMPTY_DB); setCcore(CCORE_EMPTY); toast.error("Your session has ended. Please sign in again."); };
    window.addEventListener("nyk-session-ended", onEnd);
    const t = setInterval(() => { if (Object.keys(ticketRef.current).length) refreshFileTicket(Object.keys(ticketRef.current).map((path) => path.split("/")[3])); }, 8 * 60 * 1000);
    const cleanup = () => { window.removeEventListener("nyk-session-ended", onEnd); clearInterval(t); };
    if (window.location.hash?.includes("session_id=")) { setBooting(false); return cleanup; }
    // Session tokens live in HttpOnly cookies (invisible to JS): probe the session,
    // refreshing once if the short-lived access cookie has lapsed.
    (async () => {
      try {
        await axios.get(`${API}/auth/me`, { _skipAuthRetry: true });
      } catch (e) {
        if (e?.response?.status !== 401) { setAuth(null); return; }
        try { await axios.post(`${API}/auth/refresh`, {}, { _skipAuthRetry: true }); }
        catch { setAuth(null); return; }
      }
      await loadWorkspace().catch(() => setAuth(null));
    })().finally(() => setBooting(false));
    return cleanup;
    // eslint-disable-next-line
  }, []);

  const serverRole = scope.role;
  const effectiveRole = scope.demoControls && personaRole ? personaRole : serverRole;
  const persona = useMemo(() => {
    const p = PERSONAS.find((x) => x.role === effectiveRole);
    const me = (db.users || []).find((u) => u.id === scope.dirId);
    return { role: effectiveRole || "—", name: me?.name || auth?.name || p?.name || "", org: db.org?.name || p?.org || "" };
  }, [effectiveRole, db.users, db.org, scope.dirId, auth]);
  const setPersona = (role) => {
    if (!scope.demoControls) return;
    setPersonaRole(role);
    localStorage.setItem(PERSONA_KEY, role);
  };
  // Server permissions are authoritative; a demo persona may only narrow them.
  const can = (perm) => (scope.permissions || []).includes(perm) && (effectiveRole === serverRole || hasPerm(effectiveRole, perm));
  const isClient = effectiveRole === "Client";
  const clientScopeId = isClient ? (scope.clientId || "c_govindu") : null;

  // --- Auth ---
  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data.mfaRequired) { sessionStorage.setItem("nyk_mfa_challenge", JSON.stringify(data)); return data; }
    return loadWorkspace();
  };
  const register = async (payload) => {
    const { data } = await axios.post(`${API}/auth/register`, payload);
    if (data.mfaRequired) { sessionStorage.setItem("nyk_mfa_challenge", JSON.stringify(data)); return data; }
    return loadWorkspace();
  };
  const loginWithGoogle = async (sessionId) => {
    const { data } = await axios.post(`${API}/auth/google/session`, {}, { headers: { "X-Session-ID": sessionId } });
    if (data.mfaRequired) { sessionStorage.setItem("nyk_mfa_challenge", JSON.stringify(data)); return data; }
    return loadWorkspace();
  };
  const logout = async ({ everywhere = false } = {}) => {
    try { await axios.post(`${API}/auth/${everywhere ? "logout-all" : "logout"}`, {}); } catch (e) { /* session may already be gone */ }
    ticketRef.current = {};
    setAuth(null);
    setDb(EMPTY_DB);
    setCcore(CCORE_EMPTY);
  };
  const completeMfa = async () => { sessionStorage.removeItem("nyk_mfa_challenge"); return loadWorkspace(); };
  const listSessions = () => axios.get(`${API}/auth/sessions`).then((r) => r.data);

  // --- Generic CRUD (optimistic UI + backend persistence + safe undo + optimistic concurrency) ---
  const setRows = (collection, fn) => setDb((prev) => ({ ...prev, [collection]: fn(prev[collection] || []) }));
  const putRow = (collection, record) => setRows(collection, (rows) => (rows.some((r) => r.id === record.id) ? rows.map((r) => (r.id === record.id ? record : r)) : [record, ...rows]));
  const denied = (e, fallback) => {
    const d = e?.response?.data?.detail;
    return e?.response?.status === 403 ? (typeof d === "string" ? d : "You are not permitted to do this.") : fallback;
  };

  const create = (collection, obj) => {
    const prefix = collection.slice(0, 2);
    const record = { id: obj.id || uid(prefix), createdAt: nowISO(), createdBy: scope.dirId, version: 1, ...obj };
    setRows(collection, (rows) => [record, ...rows]);
    if (PERSIST.has(collection)) {
      axios.post(`${API}/data/${collection}`, record)
        .then(({ data }) => { if (data && data.id) putRow(collection, data); })
        .catch((e) => {
          toast.error(denied(e, "Could not save. Reverting."));
          setRows(collection, (rows) => rows.filter((r) => r.id !== record.id));
        });
    }
    return record;
  };

  const update = (collection, id, patch, { silent = false, local = false } = {}) => {
    const before = (db[collection] || []).find((r) => r.id === id);
    const meta = { ...patch, updatedAt: nowISO(), updatedBy: scope.dirId };
    setRows(collection, (rows) => rows.map((r) => (r.id === id ? { ...r, ...meta } : r)));
    if (!PERSIST.has(collection) || local) return;
    const body = collection === "notifications" ? { read: patch.read } : { ...patch, expectedVersion: before?.version ?? undefined };
    axios.patch(`${API}/data/${collection}/${id}`, body)
      .then(({ data }) => {
        setRows(collection, (rows) => rows.map((r) => (r.id === id ? { ...r, version: data.version } : r)));
        if (silent || !before) return;
        const previous = Object.fromEntries(Object.keys(patch).map((k) => [k, before[k]]));
        toast("Changes saved", {
          id: `undo-${collection}-${id}`, duration: UNDO_MS, description: "Made a mistake? You can undo.",
          action: { label: "Undo", onClick: () => { update(collection, id, previous, { silent: true }); toast.success("Change undone."); } },
        });
      })
      .catch((e) => {
        if (e?.response?.status === 409) {
          const cur = e.response.data?.detail?.current;
          toast.error("This record was changed by someone else. Showing the latest version.");
          if (cur) putRow(collection, cur); else if (before) putRow(collection, before);
          return;
        }
        toast.error(denied(e, "Could not save changes. Reverted."));
        if (before) setRows(collection, (rows) => rows.map((r) => (r.id === id ? before : r)));
      });
  };

  const restore = async (collection, id) => {
    const { data } = await axios.post(`${API}/data/${collection}/${id}/restore`);
    putRow(collection, data);
    return data;
  };

  const remove = (collection, id, label = "Item") => {
    const before = (db[collection] || []).find((r) => r.id === id);
    setRows(collection, (rows) => rows.filter((r) => r.id !== id));
    if (!PERSIST.has(collection)) return;
    axios.delete(`${API}/data/${collection}/${id}`)
      .then(() => toast(`${label} moved to Trash`, {
        id: `undo-${collection}-${id}`, duration: UNDO_MS, description: "Restorable from Trash for 30 days.",
        action: { label: "Undo", onClick: () => restore(collection, id).then(() => toast.success(`${label} restored.`)).catch(() => toast.error("Could not restore.")) },
      }))
      .catch((e) => {
        toast.error(denied(e, e?.response?.status === 423 ? "This record is under legal hold." : "Could not delete. Reverted."));
        if (before) putRow(collection, before);
      });
  };

  const fetchTrash = async (collection) => {
    const { data } = await axios.get(`${API}/trash`, { params: collection ? { collection } : {} });
    return collection ? data[collection] || [] : data;
  };

  // --- PERF-01: bounded bootstrap + on-demand server reads merged into the local workspace ---
  const mergeRows = (collection, items) => setRows(collection, (rows) => {
    const byId = new Map(rows.map((r) => [r.id, r]));
    items.forEach((it) => byId.set(it.id, { ...(byId.get(it.id) || {}), ...it }));
    return [...byId.values()];
  });
  const loadMore = async (collection, params = {}) => {
    const { data } = await axios.get(`${API}/data/${collection}`, { params });
    mergeRows(collection, data.items || []);
    return data;
  };
  // When bootstrap was truncated, fetch the complete record of one matter before showing its detail page.
  const ensureMatterData = async (matterId) => {
    const truncated = Object.keys(bootMeta.truncated || {}).filter((k) => ["parties", "hearings", "tasks", "documents", "orders", "notes", "communications", "drafts"].includes(k));
    if (!matterId || !truncated.length || matterLoads.current.has(matterId)) return;
    matterLoads.current.add(matterId);
    await Promise.all(truncated.map((coll) => loadMore(coll, { matterId, limit: 500 }).catch(() => null)));
  };
  const insertLocal = (collection, record) => putRow(collection, record);
  const search = (q) => axios.get(`${API}/search`, { params: { q } }).then((r) => r.data.results);

  // --- Documents: real uploads ---
  const uploadDocument = async ({ file, matterId, category, onProgress }) => {
    const data = await uploadChunked({ file, matterId, category, onProgress });
    putRow("documents", data);
    return data;
  };
  const attachFile = async ({ file, matterId, attachedTo, onProgress }) => {
    const data = await uploadChunked({ file, matterId, category: "Media", attachedTo, onProgress });
    putRow("documents", data);
    return data;
  };
  const storageUsage = useCallback(() => axios.get(`${API}/storage/usage`).then((r) => r.data), []);
  // Short-lived, object-viewing ticket (never the session bearer token) for inline URLs.
  const fileUrl = (doc) => {
    const docId = typeof doc === "object" ? doc.id : doc;
    const path = `/api/documents/${docId}/file`;
    const cached = ticketRef.current[path];
    if (!cached || cached.exp < Date.now()) {
      ticketRef.current[path] = { pending: true, exp: Date.now() + 10000 };
      Promise.resolve().then(() => refreshFileTicket([docId]));
    }
    return cached?.ticket ? `${process.env.REACT_APP_BACKEND_URL}${path}?t=${cached.ticket}` : "";
  };

  // --- Jev decisions ---
  const ai = {
    scoreTask: (body) => axios.post(`${API}/ai/score-task`, body).then((r) => r.data).catch(() => ({ ai: false })),
    classifyOutcome: (body) => axios.post(`${API}/ai/classify-outcome`, body).then((r) => r.data).catch(() => ({ ai: false })),
    classifyDocument: (body) => axios.post(`${API}/ai/classify-document`, body).then((r) => r.data).catch(() => ({ ai: false })),
  };

  // --- Notifications ---
  const pushNotification = (n) => {
    const record = { id: uid("nt"), time: nowISO(), read: false, ...n };
    setDb((prev) => ({ ...prev, notifications: [record, ...prev.notifications] }));
    axios.post(`${API}/data/notifications`, record).catch(() => {});
  };
  const markRead = (id) => {
    setDb((prev) => ({ ...prev, notifications: prev.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)) }));
    axios.patch(`${API}/data/notifications/${id}`, { read: true }).catch(() => {});
  };
  const markAllRead = () => {
    setDb((prev) => ({ ...prev, notifications: prev.notifications.map((n) => ({ ...n, read: true })) }));
    axios.post(`${API}/data/notifications/mark-all-read`).catch(() => {});
  };

  // --- Demo data controls (server decides availability; hidden in production) ---
  const resetDemo = async () => {
    if (!scope.demoControls) { toast.error("Demo controls are not available in this environment."); return; }
    try { await axios.post(`${API}/dev/reseed`); await loadWorkspace(); toast.success("Demo data reset."); }
    catch (e) { toast.error(denied(e, "Could not reset demo data.")); }
  };
  const seedDemo = resetDemo;
  const clearChanges = async () => { await loadWorkspace(); };

  const value = {
    db, auth, scope, booting, bootMeta, ccore, switchOrg, createOrg, ccoreCan, reloadCcore, login, register, loginWithGoogle, completeMfa, logout, listSessions, loadWorkspace,
    persona, personaRole: effectiveRole, serverRole, setPersona, can, isClient, clientScopeId,
    create, update, remove, restore, fetchTrash, loadMore, ensureMatterData, insertLocal, search, uploadDocument, attachFile, storageUsage, fileUrl, ai,
    pushNotification, markRead, markAllRead,
    resetDemo, seedDemo, clearChanges,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
